import * as React from "react";
import { ChangeEvent, FC, useEffect, useMemo, useRef, useState } from "react";
import {
  MaterialReactTable,
  MRT_ToggleFullScreenButton,
  type MRT_ColumnDef,
  MRT_SortingState,
  MRT_ExpandedState,
  MRT_Row,
  MRT_Cell,
} from "material-react-table";

import {
  Box,
  Button,
  Grid,
  IconButton,
  InputBase,
  Paper,
  Table,
  TableBody,
  TableHead,
  Tooltip,
  ArrowBackIosNewOutlined as ArrowBackIosNewOutlinedIcon,
  Close as CloseIcon,
} from "../../../common/utils/mui-imports";
import HPRCheckBox from "../../../components/common/HPRCheckBox/HPRCheckBox";
import {
  Bag,
  CardAdd,
  Clock,
  Maximize2,
  Refresh,
  SearchNormal1,
  ArrowDown2,
  ReceiptSearch,
  Eye,
} from "iconsax-react";
import {
  RemediationTrackerTaskGridData,
  SortDropDownDTO,
} from "../../../common/types/common.dto.types";
import {
  ListColumn,
  ViewRemediationTrackerTaskListProps,
  ViewRemediationTrackerTaskListToolBarProps,
} from "./ViewRemediationTrackerTaskList.types";
import dayjs from "dayjs";
import {
  EnumPageType,
  EnumRemediationTrackerTaskColumnType,
  EnumRemediationTrackerTaskStatus,
  EnumSortTypes,
} from "../../../common/enums/common.enums";
import { useAppDispatch, useAppSelector } from "../../../core/hooks";
import { openPopup } from "../../../components/Popups/Popup.reducer";
import { EnumPopupType } from "../../../components/Popups/Popup.enum";
import {
  EnumSystemFunctionTypes,
  EnumSystemFunctions,
} from "../../../core/reducers/functionRights.types";
import { CheckFunctionRights } from "../../../common/functions/common.functions";
import ViewRemediationTrackerTaskTableToolBar from "./ViewRemediationTrackerTaskTableToolBar";
import { viewInspectionQuestionSliceReducerActions } from "../ViewInspectionQuestion/ViewInspectionQuestion.reducer";
import { viewRemediationTrackerTaskHistorySliceReducerActions } from "../ViewRemediationTrackerTaskHistory/ViewRemediationTrackerTaskHistory.reducer";
import { ViewRemediationTrackerTaskHistoryProps } from "../ViewRemediationTrackerTaskHistory/ViewRemediationTrackerTaskHistory.types";
import { useNavigate } from "react-router-dom";
import { RemediationTrackerTaskGridDataType } from "../../pages/RemediationTrackerTasksPage/RemediationTrackerTasksPage.types";
import ViewBulkDetailsPopup from "../../../components/Popups/ViewBulkDetailsPopup/ViewBulkDetailsPopup";
import { HPRGridColDef } from "../../../components/common/HPRDataGrid/HPRDataGrid.types";

// Table Custom Component <Start>
const setDateToString = (_date: any): string => {
  if (!_date) return "-";

  const date = dayjs(_date).format("DD-MMM-YYYY");
  return date !== "Invalid Date" ? date : "-";
};

const getStatusColorClass = (
  status: EnumRemediationTrackerTaskStatus
): string => {
  let colorClass = "";

  if (status === EnumRemediationTrackerTaskStatus.New) {
    colorClass = "blue";
  } else if (status === EnumRemediationTrackerTaskStatus.PendingReviewSME) {
    colorClass = "purple";
  } else if (status === EnumRemediationTrackerTaskStatus.PendingReviewSite) {
    colorClass = "site";
  }
  // else if (status === EnumRemediationTrackerTaskStatus.ReviewCompleted) {
  //   colorClass = "not-started";
  // }
  else if (status === EnumRemediationTrackerTaskStatus.ActionInProgress) {
    colorClass = "yellow";
  } else if (
    status === EnumRemediationTrackerTaskStatus.PendingClosureApproval
  ) {
    colorClass = "pendding-closure";
  } else if (status === EnumRemediationTrackerTaskStatus.Closed) {
    colorClass = "green";
  } else if (status === EnumRemediationTrackerTaskStatus.Overdue) {
    colorClass = "red";
  } else if (status === EnumRemediationTrackerTaskStatus.AutoClosed) {
    colorClass = "auto-closed";
  } else if (status === EnumRemediationTrackerTaskStatus.ClosedAsOverdue) {
    colorClass = "overdue-closed";
  } else if (status === EnumRemediationTrackerTaskStatus.Cancelled) {
    colorClass = "remediation-cancelled";
  }

  return colorClass;
};

//get CAPEX amound with thousand seperator
const currencyFormatter = new Intl.NumberFormat("en-US", {
  //style: "number",
  currency: "USD",
});

const getCAPEXAmount = (capexAmount: number): string => {
  const amount = currencyFormatter.format(capexAmount);

  if (amount && amount !== "0") {
    return amount;
  } else {
    return "0";
  }
};

const handleCheckBoxClick = (event: any) => {
  event.stopPropagation();
};

const TableCheckAllCheckBoxComponent = (props: {
  taskGridData?: Array<RemediationTrackerTaskGridDataType>;
  className?: string;
  onCheckBoxChange: (isChecked: boolean) => void;
  onClickRefreshBtn: () => void;
}) => {
  // set check box disabled state
  const isDisabled = props.taskGridData?.every(
    (task) =>
      task?.ParentTask?.StatusId === EnumRemediationTrackerTaskStatus.Closed ||
      task?.ParentTask?.StatusId ===
        EnumRemediationTrackerTaskStatus.ClosedAsOverdue ||
      task?.ParentTask?.StatusId === EnumRemediationTrackerTaskStatus.Cancelled
  );

  // set checked or indeterminate
  const isChecked =
    props.taskGridData?.length > 0 &&
    props.taskGridData?.every(
      (task) => task?.ParentTask?.TaskIsChecked ?? false
    );

  const isIndeterminate =
    props.taskGridData?.some(
      (task) =>
        task?.ParentTask?.TaskIsChecked ||
        task?.ChildTask?.some((some) => some.TaskIsChecked)
    ) && !isChecked;

  return (
    <>
      <div className="refresh-wrapper">
        <HPRCheckBox
          Id={`RT_CheckAll`}
          Name={`RT_CheckAll`}
          ClassName={props.className}
          onCheckBoxChange={(name: string, IsChecked: boolean) => {
            props.onCheckBoxChange(IsChecked);
          }}
          checked={isChecked ?? false}
          indeterminateChecked={isIndeterminate ?? false}
          Disabled={isDisabled ?? false}
          TooltipsTitle={isChecked ? "Deselect All" : "Select All"}
        />
        <Tooltip title="Refresh Grid">
          <div className="refresh-btn-grid position-abs">
            <Button
              onClick={() => {
                props.onClickRefreshBtn();
              }}
            >
              <Refresh size="20" color="#FF8A65" ml-2 />
            </Button>
          </div>
        </Tooltip>
      </div>
    </>
  );
};

const TableCellCheckBoxWithIconComponent = (props: {
  row: MRT_Row<RemediationTrackerTaskGridDataType>;
  functionRight: { AddBtn: boolean };
  className?: string;
  onCheckBoxChange: (parentTaskId: number, isSelected: boolean) => void;
  onclickAddNewTaskCallBack: (task: RemediationTrackerTaskGridData) => void;
}) => {
  const { original } = props?.row ?? {};
  const childTasks = original?.ChildTask ?? [];
  const parentTaskId = original?.ParentTask?.ParentTaskId;

  const excludedStatuses = new Set([
    EnumRemediationTrackerTaskStatus.Closed,
    EnumRemediationTrackerTaskStatus.ClosedAsOverdue,
    EnumRemediationTrackerTaskStatus.Cancelled,
  ]);

  const isDisabledCheckBox = [
    EnumRemediationTrackerTaskStatus.Closed,
    EnumRemediationTrackerTaskStatus.ClosedAsOverdue,
    EnumRemediationTrackerTaskStatus.Cancelled,
  ].includes(original?.ParentTask.StatusId);

  const childTaskList: RemediationTrackerTaskGridData[] = childTasks.filter(
    (task) => !excludedStatuses.has(task?.StatusId)
  );

  // set checked or indeterminate
  let isChecked = props.row?.original?.ParentTask?.TaskIsChecked;
  let isIndeterminate = false;

  if (childTaskList?.length > 0) {
    isChecked =
      original?.ParentTask?.TaskIsChecked ||
      childTaskList.every((task) => task?.TaskIsChecked);

    isIndeterminate =
      childTaskList?.some((task) => task?.TaskIsChecked) && !isChecked;
  }

  const showExpandIcon = () =>
    !original?.ParentTask?.IsAutoCreate &&
    original.ParentTask.NoOfChildTask > 1;

  return (
    <div className="fx fx-align-center ml-2">
      <Tooltip title="Add" placement="bottom">
        <div
          className="mr-10 pt-5 text-center add-new-task"
          style={{
            width: "22px",
            visibility: `${props.functionRight.AddBtn ? "visible" : "hidden"}`,
          }}
          onClick={() => {
            props.onclickAddNewTaskCallBack(original?.ParentTask);
          }}
        >
          <span className="add-icon add-new-task">
            <CardAdd size="22" color="#6a6a6a" />
          </span>
        </div>
      </Tooltip>
      <div
        className="mr-10 pt-5 text-center expand-rotate"
        style={{ width: "40px" }}
      >
        {TableCellIcon({ row: props?.row })}
        {showExpandIcon() ? (
          <ArrowDown2 size="18" />
        ) : (
          <div style={{ width: "18px" }}></div>
        )}
      </div>
      <div style={{ width: "50px", minWidth: "50px", maxWidth: "50px" }}>
        <HPRCheckBox
          Id={`Chk_${parentTaskId}`}
          Name={`Chk_${parentTaskId}`}
          ClassName={props.className}
          onCheckBoxChange={(name: string, IsChecked: boolean) => {
            props.onCheckBoxChange(
              original.ParentTask?.ParentTaskId,
              IsChecked
            );
          }}
          checked={isChecked}
          indeterminateChecked={isIndeterminate}
          Disabled={isDisabledCheckBox}
          onCheckBoxClick={handleCheckBoxClick}
        />
      </div>
    </div>
  );
};

const TableCellComponent = (props: {
  value: any;
  showToolTip?: boolean;
  className?: string;
}) => {
  return props.showToolTip ? (
    <Tooltip title={!props.value || props.value === "-" ? "" : props.value}>
      <span className={`${props.className}`}>{props.value}</span>
    </Tooltip>
  ) : (
    <span className={props.className}>{props.value}</span>
  );
};

const TableCellComponentForInspectionQuestion = (props: {
  cell: MRT_Cell<RemediationTrackerTaskGridDataType>;
  onClickViewInspectionQuestion: (inspectionQuestionId: number) => void;
}) => {
  return (
    <div className="flex--container fx-align-center">
      <div
        className="flex--item--vcenter"
        style={{ width: "50px", minWidth: "50px", maxWidth: "50px" }}
      >
        {TableCellComponent({
          value: props.cell.row.original?.ParentTask?.QuestionNumbers || "-",
        })}{" "}
      </div>
      {/* <div className="flex--item--inherit">
        {!props.cell.row.original?.ParentTask?.IsMergeTask && (
          <Tooltip title="View Question">
            <IconButton
              id={`view-btn_${props.cell.row.original?.ParentTask?.ParentTaskId}`}
              onClick={(event) => {
                event.stopPropagation();
                props.onClickViewInspectionQuestion(
                  props.cell.row.original?.ParentTask?.InspectionQuestionId
                );
              }}
            >
              <Eye size="18" color="#FF8A65" />
            </IconButton>
          </Tooltip>
        )}
      </div> */}
    </div>
  );
};
// Detail view
// const TableCellComponentForDetailView = (props: {
//   cell: any;
//   onClickViewInspectionQuestion: (inspectionQuestionId: number) => void;
// }) => {
//   return (
//     <div className="flex--container fx-align-center">
//       <div
//         className="flex--item--vcenter"
//         style={{ width: "50px", minWidth: "50px", maxWidth: "50px" }}
//       >
//         {TableCellComponent({ value: props.cell.row.original?.Q_No || "-" })}{" "}
//         <Tooltip title="View Details">
//           <IconButton>
//             <ReceiptSearch size="18" color="#219ebc" />
//           </IconButton>
//         </Tooltip>
//       </div>
//     </div>
//   );
// };

const TableToolbarComponent = (props: {
  table: any;
  toolBarProps: ViewRemediationTrackerTaskListToolBarProps;
  listColumnsState: Array<ListColumn>;
  ToolBarRef?: any;
  isFullScreen?: boolean;
  SearchValue?: string;
  onChangeSwitch: (
    event: ChangeEvent<HTMLInputElement>,
    checked: boolean
  ) => void;
  onChangeColumnDisplayOrder: (startIndex: number, endIndex: number) => void;
  onChangeShowAllColumn: (showAllColumn: boolean) => void;
  onChangeGlobalFilter?: (searchValue: string) => void;
  onKeyPressGlobalFilter?: (event: any) => void;
  onGlobalFilterBtnClick?: () => void;
}) => {
  // use for page navigation
  const navigate = useNavigate();

  const onclickBackButton = () => {
    navigate("/");
  };

  // Set Search Clear Icon
  const [isMouseOver, setIsMouseOver] = useState(false);

  const handleMouseEnter = () => {
    setIsMouseOver(true);
  };

  const handleMouseLeave = () => {
    setIsMouseOver(false);
  };

  return (
    <div className="fx width100-p rem-tr-res v-align" ref={props.ToolBarRef}>
      {/* show when not fullscreen */}
      {!props.isFullScreen && (
        <Grid container className="checkbox-filter-wrap">
          <div className="checkbox-filter">
            <HPRCheckBox
              Id={"ShowTasksAssignedToMe"}
              Name={"ShowTasksAssignedToMe"}
              Label={"Task(s) assigned to me"}
              checked={
                props.toolBarProps?.FilterCheckboxsProps
                  ?.CheckedTasksAssignedToMe
              }
              Disabled={
                props.toolBarProps?.FilterCheckboxsProps
                  ?.CheckedTasksPendingMyApproval
              }
              onCheckBoxChange={
                props.toolBarProps?.FilterCheckboxsProps?.onFilterCheckboxChange
              }
              ClassName={"inline-single-element"}
            ></HPRCheckBox>
            <HPRCheckBox
              Id={"ShowTasksPendingMyApproval"}
              Name={"ShowTasksPendingMyApproval"}
              Label={"Task(s) pending my approval"}
              checked={
                props.toolBarProps?.FilterCheckboxsProps
                  ?.CheckedTasksPendingMyApproval
              }
              onCheckBoxChange={
                props.toolBarProps?.FilterCheckboxsProps?.onFilterCheckboxChange
              }
              Disabled={
                props.toolBarProps?.FilterCheckboxsProps
                  ?.CheckedTasksAssignedToMe
              }
              ClassName={"inline-single-element"}
            ></HPRCheckBox>
            <HPRCheckBox
              Id={"ShowCompletedTasks"}
              Name={"ShowCompletedTasks"}
              Label={"Include Closed"}
              checked={
                props.toolBarProps?.FilterCheckboxsProps?.CheckedCompletedTasks
              }
              onCheckBoxChange={
                props.toolBarProps?.FilterCheckboxsProps?.onFilterCheckboxChange
              }
              Disabled={
                props.toolBarProps?.FilterCheckboxsProps
                  ?.CheckedTasksAssignedToMe ||
                props.toolBarProps?.FilterCheckboxsProps
                  ?.CheckedTasksPendingMyApproval
              }
              ClassName={"inline-single-element"}
            ></HPRCheckBox>

            <HPRCheckBox
              Id={"ShowCancelledTasks"}
              Name={"ShowCancelledTasks"}
              Label={"Include Cancelled"}
              checked={
                props.toolBarProps?.FilterCheckboxsProps?.CheckedCancelledTasks
              }
              onCheckBoxChange={
                props.toolBarProps?.FilterCheckboxsProps?.onFilterCheckboxChange
              }
              Disabled={
                props.toolBarProps?.FilterCheckboxsProps
                  ?.CheckedTasksAssignedToMe ||
                props.toolBarProps?.FilterCheckboxsProps
                  ?.CheckedTasksPendingMyApproval
              }
              ClassName={"inline-single-element"}
            ></HPRCheckBox>
          </div>
        </Grid>
      )}

      {/* show when is fullscreen */}
      {props.isFullScreen && (
        <div className="full-view-header">
          <div className="flex--container fx-wrap width100-p top-main-border mb-10 rem-btn-wrap">
            <div className="flex__item--inherit pr-10">
              <h2>
                <Button
                  aria-label="back"
                  className="sub-head-back-btn"
                  onClick={onclickBackButton}
                  title="Back to home page"
                >
                  <ArrowBackIosNewOutlinedIcon className="" />
                  <span className="body-bold secondary-color">Back</span>
                </Button>
              </h2>
            </div>
            <div className="global-header-wrap rem-trak-head-res ml-20">
              <h1>Remediation Tracker</h1>
            </div>
            <div className="flex__item search-bar--small mr-10 pr-20 fx-around">
              <Paper
                component="form"
                sx={{
                  p: "2px 4px",
                  display: "flex",
                  alignItems: "center",
                  width: "100%",
                }}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
              >
                <InputBase
                  value={props?.SearchValue ?? ""}
                  onChange={(event) =>
                    props.onChangeGlobalFilter(event.target?.value)
                  }
                  onKeyPress={props.onKeyPressGlobalFilter}
                  sx={{ ml: 1, flex: 1 }}
                  placeholder="Search Remediation Tasks"
                  inputProps={{ "aria-label": "TaskName" }}
                  // type="search"
                />
                {isMouseOver &&
                  props?.SearchValue &&
                  props?.SearchValue?.length > 0 && (
                    <Tooltip title="Clear">
                      <IconButton
                        className="mr-0"
                        onClick={() => props.onChangeGlobalFilter("")}
                      >
                        <CloseIcon
                          style={{ color: "#219ebc", fontSize: "20px" }}
                        />
                      </IconButton>
                    </Tooltip>
                  )}

                <Tooltip title="Search">
                  <IconButton
                    type="button"
                    sx={{ p: "10px" }}
                    aria-label="search"
                    className="m-0"
                    onClick={props.onGlobalFilterBtnClick}
                  >
                    <SearchNormal1
                      variant="Outline"
                      className="ico-secondary"
                      size={18}
                    />
                  </IconButton>
                </Tooltip>
              </Paper>
            </div>
          </div>
          {/* <div className="flex__item--inherit pr-10">
            <h2>
              <Button
                aria-label="back"
                className="sub-head-back-btn"
                onClick={onclickBackButton}
                title="Back to home page"
              >
                <ArrowBackIosNewOutlinedIcon className="" />
                <span className="body-bold secondary-color">Back</span>
              </Button>
            </h2>
            <div className="global-header-wrap">
              <h1>Remediation Tracker</h1>
            </div>
          </div>

          <div className="flex__item search-bar--small ml-10 mr-10">
            <Paper
              component="form"
              sx={{
                p: "2px 4px",
                display: "flex",
                alignItems: "center",
                width: "100%",
              }}
            >
              <InputBase
                value={props?.SearchValue ?? ""}
                onChange={props.onChangeGlobalFilter}
                onKeyPress={props.onKeyPressGlobalFilter}
                sx={{ ml: 1, flex: 1 }}
                placeholder="Search Remediation Tasks"
                inputProps={{ "aria-label": "TaskName" }}
                type="search"
              />
              <IconButton
                type="button"
                sx={{ p: "10px" }}
                aria-label="search"
                className="m-0"
                onClick={props.onGlobalFilterBtnClick}
              >
                <SearchNormal1
                  variant="Outline"
                  className="ico-secondary"
                  size={18}
                />
              </IconButton>
            </Paper>
          </div> */}
        </div>
      )}
      <div className="d-flex rem-trc-expo-btn-wrap">
        <div className="">
          <MRT_ToggleFullScreenButton
            table={props.table}
            className="rem-toggle-btn"
          />
        </div>
        <div className="btn-sec">
          <ViewRemediationTrackerTaskTableToolBar
            /*  ToolBarRef={props.ToolBarRef} */
            ViewRemediationTrackerTaskTableToolBarButtonProps={{
              ...props.toolBarProps
                .ViewRemediationTrackerTaskTableToolBarButtonProps,
              isFullScreen: props.isFullScreen,
            }}
            ViewRemediationTrackerTaskListToolbarReportProps={
              props.toolBarProps
                ?.ViewRemediationTrackerTaskListToolbarReportProps
            }
            ViewRemediationTrackerTaskListToolbarColumnProps={{
              ...props.toolBarProps
                .ViewRemediationTrackerTaskListToolbarColumnProps,
              ListColumns: props.listColumnsState,
              OnChangeSwitch: props.onChangeSwitch,
              onChangeColumnDisplayOrder: props.onChangeColumnDisplayOrder,
              onChangeShowAllColumn: props.onChangeShowAllColumn,
            }}
          />
        </div>
      </div>
    </div>
  );
};

const TableBottomToolbarComponent = (props: { legendHeight }) => {
  return (
    <div
      className="flex--container legend-table"
      /* ref={props.legendHeight} */
      style={{ flexWrap: "wrap" }}
    >
      {/* legend */}
      <div className="flex--container pr-20">
        <div className="flex--item--vcenter mr-5">
          <Clock size="20" color="#FF8A65" />
        </div>
        <div className="flex--item--vcenter">
          <span>Approval History</span>
        </div>
      </div>
      {/* <div className="flex--container pr-20">
        <div className="flex--item--vcenter mr-5">
          <Settings size="20" color="#FF8A65" />
        </div>
        <div className="flex--item--vcenter">
          <span>One to One</span>
        </div>
      </div> */}
      {/* <div className="flex--container pr-20">
        <div className="flex--item--vcenter mr-5">
          <Data size="20" color="#FF8A65" />
        </div>
        <div className="flex--item--vcenter">
          <span>One to Many</span>
        </div>
      </div> */}
      {/* <div className="flex--container pr-20">
        <div className="flex--item--vcenter mr-5">
          <Data size="20" color="#FF8A65" className="rotate" />
        </div>
        <div className="flex--item--vcenter">
          <span>Many to One</span>
        </div>
      </div> */}
      <div className="flex--container pr-20">
        <div className="flex--item--vcenter mr-5">
          <Maximize2 size="20" color="#FF8A65" />
        </div>
        <div className="flex--item--vcenter">
          <span>Toggle full screen</span>
        </div>
      </div>
      <div className="flex--container pr-20">
        <div className="flex--item--vcenter mr-5">
          <Refresh size="20" color="#FF8A65" />
        </div>
        <div className="flex--item--vcenter">
          <span>Refresh Grid</span>
        </div>
      </div>
    </div>
  );
};

const TableDetailPanelComponent = (props: {
  row: RemediationTrackerTaskGridDataType;
  functionRight: { deleteBtn: boolean; AddBtn: boolean };
  onDeleteRemediationTrackerTask: (
    taskId: number,
    event: React.MouseEvent
  ) => void;
  onChildTaskCheckBoxClicked: (
    parentTaskId: number,
    childTaskId: number,
    isChecked: boolean
  ) => void;
  onClickViewOrEditTask: (taskId: number, isParentTask: boolean) => void;
  onViewRemediationTrackerTaskHistory: (
    event: React.MouseEvent<HTMLButtonElement>,
    taskId: number,
    identifier: string
  ) => void;
  onClickOpenViewDetailsPopup: (
    task: RemediationTrackerTaskGridData,
    isParentTask: boolean
  ) => void;
}) => {
  const onClickViewOrEditTask = (
    event: any,
    taskId: number,
    isParentTask: boolean
  ) => {
    //skip redirect to remediation tracker task page when view Remediation Tracker Task History details
    if (
      event?.target?.parentElement?.classList?.contains(
        "viewRemediationTrackerTaskHistory"
      ) ||
      (event?.target.className?.baseVal || "").includes("viewDetailsPopUp") ||
      (event.target.classList || "").contains("viewDetailsPopUp") ||
      (event.target.parentElement?.classList || "").contains("viewDetailsPopUp")
    )
      return;

    if (props.onClickViewOrEditTask) {
      props.onClickViewOrEditTask(taskId, isParentTask);
    }
  };

  /**
   * get child tasks details
   */
  const childTasks = useMemo(() => {
    let _childTasks = [] as Array<RemediationTrackerTaskGridData>;

    if (props.row.ChildTask && props.row.ChildTask?.length > 0) {
      _childTasks = [...props.row.ChildTask];
    }

    return _childTasks;
  }, [props.row.ChildTask]);

  return (
    childTasks &&
    childTasks?.length > 0 && (
      <Box
        sx={{
          display: "grid",
          margin: "auto",
          gridTemplateColumns: "1fr",
          width: "100%",
        }}
      >
        <Table style={{ minWidth: "88vw" }}>
          <TableHead>
            <tr>
              <th
                style={{ width: "30px", maxWidth: "30px", minWidth: "30px" }}
                className="bold"
              >
                <Button
                  style={{
                    margin: 5,
                    visibility: "hidden",
                  }}
                  className="primary-btn-small"
                  disableElevation
                  size="small"
                  variant="outlined"
                  startIcon={<CardAdd variant="Outline" />}
                  onClick={() => {
                    //props.onclickAddNewTaskCallBack(props.row.original);
                  }}
                >
                  Add
                </Button>
              </th>

              <th
                style={{ width: "10px", minWidth: "10px", maxWidth: "10px" }}
              ></th>

              <th
                style={{ width: "10px", minWidth: "10px", maxWidth: "10px" }}
              ></th>

              <th style={{ width: "100%" }}>
                <tbody className="fx">
                  <th style={{ flex: 1.5 }} className="">
                    Identifier
                  </th>
                  <th style={{ flex: 4.2 }} className="">
                    Task Description
                  </th>
                  <th style={{ flex: 1.6 }} className="">
                    Category
                  </th>
                  <th style={{ flex: 0.9 }} className="">
                    Due Date
                  </th>
                  <th style={{ flex: 2 }} className="">
                    Status
                  </th>
                  <th style={{ flex: 2 }} className="">
                    Approver
                  </th>
                  <th style={{ flex: 2 }} className="">
                    Action Owner
                  </th>

                  {/* <th style={{ flex: 2 }} className="bold">
                    Pending Approvers
                  </th> */}
                </tbody>
              </th>
            </tr>
          </TableHead>
          <TableBody>
            {childTasks?.map(
              (task: RemediationTrackerTaskGridData, taskIndex) => {
                return (
                  <tr key={taskIndex}>
                    <td
                      style={{
                        width: "10px",
                        minWidth: "10px",
                        maxWidth: "10px",
                      }}
                    >
                      <div
                        className="ml-5 bold"
                        style={{ letterSpacing: "-0.75px", display: "none" }}
                      >
                        SUB
                      </div>
                    </td>
                    <td
                      style={{
                        width: "40px",
                        minWidth: "40px",
                        maxWidth: "40px",
                      }}
                    >
                      <div className="mr-10">
                        <IconButton
                          style={{
                            visibility: `${
                              task.StatusId ===
                                EnumRemediationTrackerTaskStatus.New &&
                              props.functionRight.deleteBtn &&
                              !task.IsAutoCreate
                                ? "visible"
                                : "hidden"
                            }`,
                          }}
                          onClick={(event) => {
                            props.onDeleteRemediationTrackerTask(
                              task.ChildTaskId,
                              event
                            );
                          }}
                        >
                          <Bag
                            variant="Outline"
                            className="Icon-button-delete"
                            size={20}
                          />
                        </IconButton>
                      </div>
                    </td>
                    <td
                      style={{
                        width: "50px",
                        minWidth: "50px",
                        maxWidth: "50px",
                      }}
                    >
                      <HPRCheckBox
                        Id={`childChk_${task.ChildTaskId}`}
                        Name={`childChk_${task.ChildTaskId}`}
                        ClassName={`inline-single-elemen`}
                        onCheckBoxChange={(
                          name: string,
                          IsChecked: boolean
                        ) => {
                          props.onChildTaskCheckBoxClicked(
                            task.ParentTaskId,
                            task.ChildTaskId,
                            IsChecked
                          );
                        }}
                        checked={task.TaskIsChecked}
                        Disabled={
                          task.StatusId ===
                            EnumRemediationTrackerTaskStatus.Closed ||
                          task.StatusId ===
                            EnumRemediationTrackerTaskStatus.ClosedAsOverdue ||
                          task.StatusId ===
                            EnumRemediationTrackerTaskStatus.Cancelled
                        }
                        onCheckBoxClick={handleCheckBoxClick}
                      />
                    </td>

                    <td width={"100%"}>
                      <tbody
                        className="cursor-pointer fx fx-middle"
                        onClick={(event) => {
                          onClickViewOrEditTask(event, task.ChildTaskId, false);
                        }}
                        onKeyUp={() => {}}
                      >
                        <td style={{ flex: 1.5 }}>
                          {task?.Identifier}

                          <Tooltip title="View Details">
                            <IconButton
                              className="viewDetailsPopUp"
                              onClick={() => {
                                props.onClickOpenViewDetailsPopup(task, false);
                              }}
                            >
                              <ReceiptSearch
                                size="18"
                                color="#219ebc"
                                className="viewDetailsPopUp"
                              />
                            </IconButton>
                          </Tooltip>
                        </td>
                        <td style={{ flex: 4.2 }}>
                          <Tooltip title={task?.TaskName}>
                            <div className="limit-2">
                              {task?.TaskName || "-"}
                            </div>
                          </Tooltip>
                        </td>
                        <td className="padding-unset" style={{ flex: 1.6 }}>
                          {task.Category || "-"}
                        </td>
                        <td style={{ flex: 0.9 }}>
                          {setDateToString(task?.TaskDueDate) || "-"}
                        </td>
                        <td style={{ flex: 2 }}>
                          <span
                            className={`status-color ${getStatusColorClass(
                              task.StatusId
                            )}`}
                          >
                            {task.StatusName || "-"}
                          </span>
                        </td>
                        <td
                          style={{ flex: 2 }}
                          className="viewRemediationTrackerTaskHistory"
                        >
                          {task.StatusId !==
                          EnumRemediationTrackerTaskStatus.New ? (
                            <Tooltip title="View Approver History">
                              <IconButton
                                id="history-btn"
                                className="viewRemediationTrackerTaskHistory"
                                onClick={(event) => {
                                  event.stopPropagation();
                                  props.onViewRemediationTrackerTaskHistory(
                                    event,
                                    task.ChildTaskId,
                                    task.Identifier
                                  );
                                }}
                              >
                                <Clock size="18" color="#FF8A65" />
                              </IconButton>
                            </Tooltip>
                          ) : (
                            "-"
                          )}
                        </td>
                        <td style={{ flex: 2 }}>{task.ActionOwner || "-"}</td>
                      </tbody>
                    </td>
                  </tr>
                );
              }
            )}
          </TableBody>
        </Table>
      </Box>
    )
  );
};

const TableCellIcon = (props: { row: any }) => {
  if (props.row.original?.IsMergeTask) {
    return "";
  } else if (props.row.original?.RemediationTrackerTaskIdList?.length === 1) {
    return "";
  } else if (props.row.original?.RemediationTrackerTaskIdList?.length > 1) {
    return "";
  }
};

const ViewRemediationTrackerTaskTable: FC<
  ViewRemediationTrackerTaskListProps
> = (props) => {
  const dispatch = useAppDispatch();

  // reduce state
  const authState = useAppSelector((state) => state.authReducer);
  const [isFullScreen, setIsFullScreen] = useState<boolean>(props.IsFullScreen);
  // set height
  const legendHeight = useRef(null);
  const toolBarRef = useRef(null);
  const [tableHeight, setTableHeight] = useState(0);
  useEffect(() => {
    // set page height
    let tableHeight =
      props.GridHeight -
      (toolBarRef?.current?.offsetHeight -
        19 +
        legendHeight?.current?.offsetHeight) -
      // props.AdvanceFilterHeight -
      36;

    setTableHeight(tableHeight);
    function handleResize() {
      let tableHeight =
        props.GridHeight -
        (toolBarRef?.current?.offsetHeight -
          19 +
          legendHeight?.current?.offsetHeight) -
        // props.AdvanceFilterHeight -
        36;
      setTableHeight(tableHeight);
    }

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [
    props.AdvanceFilterHeight,
    toolBarRef?.current?.offsetHeight,
    legendHeight?.current?.offsetHeight,
    props.GridHeight,
    props.IsFullScreen,
    isFullScreen,
  ]);

  // check System Function access/right
  const checkSystemFunctionRight = (
    functionId: EnumSystemFunctions,
    functionTypeId: EnumSystemFunctionTypes
  ): boolean => {
    return CheckFunctionRights(
      authState.FunctionRights,
      EnumPageType.RemediationTrackerTasks,
      functionId,
      functionTypeId
    );
  };

  const initialListColumns: Array<ListColumn> =
    props.ViewRemediationTrackerTaskListToolBar
      .ViewRemediationTrackerTaskListToolbarColumnProps.ListColumns;

  const dataFetchedRef = useRef(false);
  const columnFilterDataFetchedRef = useRef(false);

  const [listColumnsState, setListColumnsState] =
    useState<Array<ListColumn>>(initialListColumns);

  const [pagination, setPagination] = useState({
    pageIndex: props.PaginationProps?.SelectedPage
      ? props.PaginationProps?.SelectedPage - 1
      : 0,
    pageSize: props.PaginationProps?.PageSize,
  });

  const [sorting, setSorting] = useState<MRT_SortingState>([
    {
      id: `${props.ViewRemediationTrackerTaskListToolBar?.ViewRemediationTrackerTaskListSortByOptions?.InitialSortByState?.Label}-${props.ViewRemediationTrackerTaskListToolBar?.ViewRemediationTrackerTaskListSortByOptions?.InitialSortByState?.Value}`,
      desc:
        props.ViewRemediationTrackerTaskListToolBar
          ?.ViewRemediationTrackerTaskListSortByOptions?.InitialSortByState
          ?.SortType === EnumSortTypes.Descending,
    },
  ]);

  const [expanded, setExpanded] = useState<MRT_ExpandedState>({});

  // reset expand
  useEffect(() => {
    if (!props.ClearExpandedRows) return;
    setExpanded({});
  }, [props.ClearExpandedRows]);

  // set pagination
  useEffect(() => {
    setPagination({
      pageIndex: props.PaginationProps?.SelectedPage - 1,
      pageSize: props.PaginationProps?.PageSize,
    });
  }, [props.PaginationProps?.PageSize, props.PaginationProps?.SelectedPage]);

  // full screen
  useEffect(() => {
    if (props.CallbackFunction.onIsFullScreenChangeCallBack) {
      props.CallbackFunction.onIsFullScreenChangeCallBack(isFullScreen);
    }
  }, [isFullScreen]);

  // pagination
  useEffect(() => {
    // skip on load page
    dataFetchedRef.current = true;
    if (pagination.pageSize === props.PaginationProps?.PageSize) {
      if (pagination.pageIndex + 1 !== props.PaginationProps?.SelectedPage) {
        dataFetchedRef.current = false;
      }
    } else {
      dataFetchedRef.current = false;
    }

    if (dataFetchedRef.current) return;

    if (props?.PaginationProps?.onPaginationChangeCallBack) {
      props?.PaginationProps.onPaginationChangeCallBack(
        pagination.pageIndex + 1,
        pagination.pageSize
      );
    }
  }, [pagination]);

  // sorting
  useEffect(() => {
    // if (sorting?.length === 0) return;

    if (props.CallbackFunction?.onChangeSortByCallBack) {
      if (sorting?.length === 0) {
        props.CallbackFunction?.onChangeSortByCallBack({
          SortType: EnumSortTypes.Ascending,
          Value: EnumRemediationTrackerTaskColumnType.TaskId.toString(),
          Label: "TaskId",
        } as SortDropDownDTO);
      } else {
        const sort = sorting[0];

        props.CallbackFunction?.onChangeSortByCallBack({
          SortType: sort.desc
            ? EnumSortTypes.Descending
            : EnumSortTypes.Ascending,
          Value: sort?.id?.split("-")[1],
          Label: sort.id?.split("-")[0],
        } as SortDropDownDTO);
      }
    }
  }, [sorting]);

  // When create column & apply sorting then  Id sholud be equal to accessorKey-EnumRemediationTrackerTaskSortType Ex:[columnName-1]
  const columns = useMemo<MRT_ColumnDef<RemediationTrackerTaskGridDataType>[]>(
    () => [
      {
        id: "Identifier-16",
        accessorKey: "Identifier",
        header: "Identifier",
        Size: 100,
        enableColumnActions: false,
        Cell: ({ cell }) => (
          <div className="flex--container fx-align-center">
            <div className="flex--item--vcenter">
              {TableCellComponent({
                value: cell.row.original?.ParentTask?.Identifier || "-",
              })}
            </div>
            <div
              className="flex--item--inherit viewDetailsPopUp"
              onClick={() => {
                handleOpenViewDetailsPopup(cell.row.original?.ParentTask, true);
              }}
            >
              <Tooltip title="View Details">
                <IconButton className="viewDetailsPopUp">
                  <ReceiptSearch
                    size="18"
                    color="#219ebc"
                    className="viewDetailsPopUp"
                  />
                </IconButton>
              </Tooltip>
            </div>
          </div>
        ),
      },

      {
        id: "LocationName-1",
        accessorKey: "LocationName",
        header: "Location",
        enableColumnActions: false,
        Cell: ({ cell }) =>
          TableCellComponent({
            value: cell.row.original?.ParentTask?.LocationName || "-",
          }),
      },
      {
        id: "AssessmentType-20",
        accessorKey: "AssessmentType",
        header: "Assessment Type",
        enableColumnActions: false,
        Cell: ({ cell }) =>
          TableCellComponent({
            value: cell.row.original?.ParentTask?.AssessmentTypeName || "-",
          }),
      },
      {
        id: "InspectionName-2",
        accessorKey: "InspectionName",
        header: "Inspection",
        enableColumnActions: false,
        Cell: ({ cell }) =>
          TableCellComponent({
            value: cell.row.original?.ParentTask?.InspectionName || "-",
            showToolTip: true,
            className: "truncate",
          }),
      },
      {
        id: "InspectionStandardName-3",
        accessorKey: "InspectionStandardName",
        header: "Standard",
        enableColumnActions: false,
        Cell: ({ cell }) =>
          TableCellComponent({
            value: cell.row.original?.ParentTask?.InspectionStandardName || "-",
            showToolTip: true,
            className: "truncate",
          }),
      },
      {
        id: "Q_No-4",
        accessorKey: "QuestionNumbers",
        header: "Q.No",
        enableColumnActions: false,
        Cell: ({ cell }) =>
          TableCellComponentForInspectionQuestion({
            cell: cell,
            onClickViewInspectionQuestion: onClickViewInspectionQuestion,
          }),
      },
      {
        id: "Piority-8",
        accessorKey: "Piority",
        header: "Priority",
        enableColumnActions: false,
        Cell: ({ cell }) =>
          TableCellComponent({
            value: cell.row.original?.ParentTask?.PriorityName || "-",
          }),
      },
      {
        id: "DueDate-9",
        accessorKey: "DueDate",
        header: "Due Date",
        enableColumnActions: false,
        Cell: ({ cell }) =>
          TableCellComponent({
            value:
              setDateToString(cell.row?.original?.ParentTask?.TaskDueDate) ||
              "-",
          }),
      },
      {
        id: "Status-6",
        accessorKey: "Status",
        header: "Status",
        enableColumnActions: false,
        Cell: ({ cell }) =>
          TableCellComponent({
            value: cell.row.original?.ParentTask?.StatusName || "-",
            className: `status-color ${getStatusColorClass(
              cell.row.original?.ParentTask?.StatusId
            )}`,
          }),
        size: 200,
      },
      {
        id: "IsCapexRequired-11",
        accessorKey: "IsCapexRequired",
        header: "Is CAPEX Required",
        enableColumnActions: false,
        Cell: ({ cell }) =>
          TableCellComponent({
            value: cell.row.original?.ParentTask?.IsCAPEXRequired || "-",
          }),
      },
      {
        id: "CapexAmountRequired-12",
        accessorKey: "CapexAmountRequired",
        header: "CAPEX Amount Required (M$)",
        enableColumnActions: false,
        size: 200,
        Cell: ({ cell }) =>
          TableCellComponent({
            value: getCAPEXAmount(
              cell.row.original?.ParentTask?.SumOfCapexAmount
            ),
          }),
      },
      {
        id: "Category-15",
        accessorKey: "Category",
        header: "Category",
        enableColumnActions: false,
        Cell: ({ cell }) =>
          TableCellComponent({
            value: cell.row.original?.ParentTask?.Category || "-",
          }),
      },
      {
        id: "noOfTask-17",
        accessorKey: "noOfTask",
        header: "No.Of Task",
        enableColumnActions: false,
        Cell: ({ cell }) =>
          TableCellComponent({
            value: cell.row.original?.ParentTask?.NoOfChildTask?.toString(),
          }),
      },
      {
        id: "pendingApprovers-18",
        accessorKey: "pendingApprovers",
        header: "Pending Approver(s)",
        enableColumnActions: false,
        Cell: ({ cell }) =>
          TableCellComponent({
            value: cell.row.original?.ParentTask?.PendingApprovers || "-",
            className: "truncate-multiline",
            showToolTip: true,
          }),
      },
    ],
    []
  );

  // =================== Call Back Function <Start> ===========================================

  const onClickRefreshBtn = () => {
    if (props.CallbackFunction.onClickRefreshBtnCallBack) {
      props.CallbackFunction.onClickRefreshBtnCallBack();
    }
  };

  const onCheckAllCheckBoxClicked = (isChecked: boolean) => {
    if (props.CallbackFunction?.onCheckAllCheckBoxClickedCallBack) {
      props.CallbackFunction?.onCheckAllCheckBoxClickedCallBack(isChecked);
    }
  };

  const onParentTaskCheckBoxClicked = (
    parentTaskId: number,
    isChecked: boolean
  ) => {
    if (props.CallbackFunction?.onParentTaskCheckBoxClicked) {
      props.CallbackFunction?.onParentTaskCheckBoxClicked(
        parentTaskId,
        isChecked
      );
    }
  };

  const onChildTaskCheckBoxClicked = (
    parentTaskId: number,
    childTaskId: number,
    isChecked: boolean
  ) => {
    if (props.CallbackFunction?.onChildTaskCheckBoxClicked) {
      props.CallbackFunction?.onChildTaskCheckBoxClicked(
        parentTaskId,
        childTaskId,
        isChecked
      );
    }
  };

  const onClickExpandedRow = (parentTaskId: number, isExpand: boolean) => {
    if (
      isExpand &&
      props?.CallbackFunction?.onclickRemediationTrackerTaskDetails
    ) {
      props?.CallbackFunction?.onclickRemediationTrackerTaskDetails(
        parentTaskId
      );
    }
  };

  const onDeleteRemediationTrackerTask = (
    taskId: number,
    event: React.MouseEvent
  ) => {
    event.stopPropagation();
    if (props.CallbackFunction?.onDeleteRemediationTrackerTask) {
      dispatch(
        openPopup({
          Open: true,
          BodyText: "Do you want to delete the task?",
          HeaderText: "Confirmation",
          PopupType: EnumPopupType.YesNoConfirmation,
          YesBtnClick() {
            props.CallbackFunction?.onDeleteRemediationTrackerTask(taskId);
          },
          DisableBackdropClose: true,
        })
      );
    }
  };

  const onClickViewOrEditTask = (parentTaskId: number) => {
    if (props.CallbackFunction?.onClickViewOrEditTask) {
      props.CallbackFunction?.onClickViewOrEditTask(parentTaskId);
    }
  };

  const onChangeColumnOptionShowHideSwitch = (
    event: ChangeEvent<HTMLInputElement>,
    checked: boolean
  ) => {
    let filteredColumns: Array<ListColumn> = [] as Array<ListColumn>;
    listColumnsState.forEach((column) => {
      if (column.ColumnName == event.target.name) {
        filteredColumns.push({ ...column, ColumnHide: !checked });
      } else {
        filteredColumns.push(column);
      }
    });

    if (props.CallbackFunction?.onChangeColumnOptions) {
      props.CallbackFunction?.onChangeColumnOptions(filteredColumns);
    }

    setListColumnsState(filteredColumns);
  };

  const getColumnHideValue = (name: string) => {
    return !listColumnsState.find((column) => column.ColumnName == name)
      ?.ColumnHide;
  };

  const onclickAddNewTaskCallBack = (task: RemediationTrackerTaskGridData) => {
    if (props.CallbackFunction?.onclickAddNewTaskCallBack) {
      props.CallbackFunction?.onclickAddNewTaskCallBack(task);
    }
  };

  const onClickViewInspectionQuestion = (inspectionQuestionId: number) => {
    dispatch(
      viewInspectionQuestionSliceReducerActions.viewInspectionQuestionDetails({
        InspectionQuestionId: inspectionQuestionId,
      })
    );
  };

  const onChangeColumnDisplayOrder = (startIndex: number, endIndex: number) => {
    const columnList = [...listColumnsState];

    const [removed] = columnList.splice(startIndex, 1);
    columnList.splice(endIndex, 0, removed);

    if (props.CallbackFunction?.onChangeColumnOptions) {
      props.CallbackFunction?.onChangeColumnOptions(columnList);
    }

    setListColumnsState(columnList);
  };

  const onChangeShowAllColumn = (showAllColumn: boolean) => {
    const columnList = listColumnsState?.map((column) => {
      return {
        ...column,
        ColumnHide: !column.DisabledColumnHide
          ? !showAllColumn
          : column.ColumnHide,
      };
    });

    if (props.CallbackFunction?.onChangeColumnOptions) {
      props.CallbackFunction?.onChangeColumnOptions(columnList);
    }

    setListColumnsState(columnList);
  };

  const onChangeFullScreenGlobalFilter = (searchValue) => {
    if (props.CallbackFunction.onChangeFullScreenGlobalFilterCallBack) {
      props.CallbackFunction.onChangeFullScreenGlobalFilterCallBack(
        searchValue
      );
    }
  };

  const onKeyPressFullScreenGlobalFilter = (event: any) => {
    if (props.CallbackFunction.onKeyPressFullScreenGlobalFilterCallBack) {
      props.CallbackFunction.onKeyPressFullScreenGlobalFilterCallBack(event);
    }
  };

  const onFullScreenGlobalFilterBtnClick = () => {
    if (props.CallbackFunction.onFullScreenGlobalFilterBtnClickCallBack) {
      props.CallbackFunction.onFullScreenGlobalFilterBtnClickCallBack();
    }
  };

  const handleExpandedChange = (expandedState: MRT_ExpandedState) => {
    setExpanded(expandedState);
  };

  // =================== Call Back Function <End> ===========================================

  const onViewRemediationTrackerTaskHistory = (
    event: React.MouseEvent<HTMLButtonElement>,
    remediationTrackerTaskId: number,
    identifier: string
  ) => {
    dispatch(
      viewRemediationTrackerTaskHistorySliceReducerActions.viewRemediationTrackerTaskHistoryDetails(
        {
          Ref: event.currentTarget,
          RemediationTrackerTaskId: remediationTrackerTaskId,
          Identifier: identifier,
        } as ViewRemediationTrackerTaskHistoryProps
      )
    );
  };

  const onClickTableBodyRow = (
    row: MRT_Row<RemediationTrackerTaskGridDataType>,
    event?: any
  ) => {
    // handle add button click
    if (
      event?.target?.parentElement?.classList?.contains("add-new-task") ||
      (event.target.classList || "").contains("viewDetailsPopUp") ||
      (event?.target.className?.baseVal || "").includes("viewDetailsPopUp") ||
      (event.target.parentElement?.classList || "").contains("viewDetailsPopUp")
    )
      return;

    const { ParentTaskId, IsMergeTask, NoOfChildTask } =
      row.original?.ParentTask;

    !IsMergeTask && NoOfChildTask === 1
      ? onClickViewOrEditTask(ParentTaskId)
      : onClickExpandedRow(ParentTaskId, !row.getIsExpanded());
    row.toggleExpanded(!row.getIsExpanded());
  };

  const showDeleteButton = () =>
    checkSystemFunctionRight(
      EnumSystemFunctions.RemTrackerTasksPage_Delete_childTask,
      EnumSystemFunctionTypes.ButtonAccess
    );

  const showAddButton = () =>
    checkSystemFunctionRight(
      EnumSystemFunctions.RemTrackerTasksPage_AddOrMergedTask,
      EnumSystemFunctionTypes.ButtonAccess
    );

  /**
   * View Details popup
   */

  const vidwDetailsColumns: HPRGridColDef[] = [
    {
      field: "Id",
      headerName: "Id",
      hideColumn: true,
      hideable: false,
      filterable: false,
    },
    {
      field: "AssessmentTypeName",
      headerName: "Assessment Type",
      flex: 2,
      sortable: false,
      minWidth: 100,
      headerAlign: "left",
      align: "left",
    },
    {
      field: "InspectionName",
      headerName: "Inspection",
      flex: 4,
      sortable: false,
      headerAlign: "left",
      align: "left",
    },
    {
      field: "StandardName",
      headerName: "Standard",
      flex: 4,
      sortable: false,
      minWidth: 100,
      headerAlign: "left",
      align: "left",
    },
    {
      field: "QuestionNo",
      headerName: "Q.No",
      flex: 2,
      sortable: false,
      headerAlign: "left",
      align: "left",
      // renderCell: (params) => (
      //   <div className="flex--container fx-align-center">
      //     <span className="mr-5">{params.value || "-"}</span>
      //     <Tooltip title="View Question">
      //       <IconButton
      //         size="small"
      //         onClick={(event) => {
      //           event.stopPropagation();
      //           onClickViewInspectionQuestion(params.row.Id);
      //         }}
      //       >
      //         <Eye size="18" color="#FF8A65" />
      //       </IconButton>
      //     </Tooltip>
      //   </div>
      // ),
    },
  ];

  const [selectedTask, setSelectedTask] =
    useState<RemediationTrackerTaskGridData | null>(null);

  const onClickViewDetailsPopup = (taskId: number, isParentTask: boolean) => {
    if (props.CallbackFunction?.onClickOpenViewDetailsPopup) {
      props.CallbackFunction?.onClickOpenViewDetailsPopup(taskId, isParentTask);
    }
  };

  const handleOpenViewDetailsPopup = (
    task: RemediationTrackerTaskGridData,
    isParentTask: boolean
  ) => {
    setSelectedTask(task);

    let taskId = task.ParentTaskId;
    if (!isParentTask) {
      taskId = task.ChildTaskId;
    }

    onClickViewDetailsPopup(taskId, isParentTask);
  };

  const handleCloseViewDetailsPopup = () => {
    setSelectedTask(null);
    onClickViewDetailsPopup(0, false);
  };

  return (
    <div className={isFullScreen ? "enlarge-height-unset" : ""}>
      {/*VIEW DETAILS POPUP*/}
      {selectedTask && (
        <ViewBulkDetailsPopup
          open={props.ViewDetailsPopUpData?.OpenPopup}
          title="Detail View"
          subtitle={selectedTask?.LocationName}
          columns={vidwDetailsColumns}
          data={props.ViewDetailsPopUpData?.Data}
          onClose={handleCloseViewDetailsPopup}
        />
      )}

      <div className="test">
        <MaterialReactTable
          enableStickyHeader
          enableStickyFooter
          columns={columns}
          data={props.GridData}
          defaultColumn={{ maxSize: 900, minSize: 50, size: 150 }}
          renderBottomToolbarCustomActions={({ table }) =>
            TableBottomToolbarComponent({ legendHeight })
          }
          renderToolbarInternalActions={({ table }) =>
            TableToolbarComponent({
              table: table,
              toolBarProps: props.ViewRemediationTrackerTaskListToolBar,
              listColumnsState: listColumnsState,
              isFullScreen: isFullScreen,
              SearchValue: props.SearchValue,
              onChangeSwitch: onChangeColumnOptionShowHideSwitch,
              onChangeColumnDisplayOrder: onChangeColumnDisplayOrder,
              onChangeShowAllColumn: onChangeShowAllColumn,
              onChangeGlobalFilter: onChangeFullScreenGlobalFilter,
              onKeyPressGlobalFilter: onKeyPressFullScreenGlobalFilter,
              onGlobalFilterBtnClick: onFullScreenGlobalFilterBtnClick,
              ToolBarRef: toolBarRef,
            })
          }
          muiTableBodyRowProps={({ row }) => ({
            onClick: (event) => {
              event.stopPropagation();
              onClickTableBodyRow(row, event);
            },
            className: row.getIsExpanded() ? "row-expanded" : "row-collapsed",
            sx: {
              cursor: "pointer",
            },
          })}
          renderDetailPanel={({ row }) =>
            TableDetailPanelComponent({
              row: row.original,
              functionRight: {
                deleteBtn: showDeleteButton(),
                AddBtn: showAddButton(),
              },
              onChildTaskCheckBoxClicked: onChildTaskCheckBoxClicked,
              onDeleteRemediationTrackerTask: (taskId, event) =>
                onDeleteRemediationTrackerTask(taskId, event),
              onClickViewOrEditTask: onClickViewOrEditTask,
              onViewRemediationTrackerTaskHistory:
                onViewRemediationTrackerTaskHistory,
              onClickOpenViewDetailsPopup(task, isParentTask) {
                handleOpenViewDetailsPopup(task, isParentTask);
              },
            })
          }
          rowCount={props.PaginationProps.TotalRecordsCount}
          paginationDisplayMode="pages"
          muiPaginationProps={{
            rowsPerPageOptions: [10, 15, 25, 50, 100],
          }}
          onPaginationChange={setPagination}
          state={{
            pagination: pagination,
            columnVisibility: {
              "Identifier-16": getColumnHideValue("Identifier"),
              "LocationName-1": getColumnHideValue("LocationName"),
              "AssessmentType-20": getColumnHideValue("AssessmentType"),
              "InspectionName-2": getColumnHideValue("InspectionName"),
              "InspectionStandardName-3": getColumnHideValue(
                "InspectionStandardName"
              ),
              "Q_No-4": getColumnHideValue("Q_No"),
              "Piority-8": getColumnHideValue("Piority"),
              "DueDate-9": getColumnHideValue("DueDate"),
              "Status-6": getColumnHideValue("Status"),
              "IsCapexRequired-11": getColumnHideValue("IsCapexRequired"),
              "CapexAmountRequired-12": getColumnHideValue(
                "CapexAmountRequired"
              ),
              "Category-15": getColumnHideValue("Category"),
              "noOfTask-17": getColumnHideValue("noOfTask"),
              "pendingApprovers-18": getColumnHideValue("pendingApprovers"),
            },
            sorting: sorting,
            columnOrder: [
              ...["mrt-row-expand", "mrt-row-select"],
              ...listColumnsState.map((item) => item.ColumnId),
            ],
            isFullScreen: isFullScreen,
            showLoadingOverlay: props.ShowDataLoading,
            isLoading: props.ShowDataLoading,
            expanded: expanded,
          }}
          enableRowSelection
          manualPagination={true}
          enableSorting={true}
          manualSorting={true}
          muiTableContainerProps={{ style: { height: tableHeight } }}
          muiBottomToolbarProps={{
            className: "rem-leg-pag-wrapper",
            ref: legendHeight,
          }}
          onSortingChange={setSorting}
          onIsFullScreenChange={setIsFullScreen}
          positionExpandColumn="first"
          enableColumnFilters={false}
          manualFiltering={true}
          columnFilterDisplayMode="popover"
          autoResetExpanded={true}
          onExpandedChange={handleExpandedChange}
          muiSelectAllCheckboxProps={{
            component: () =>
              TableCheckAllCheckBoxComponent({
                taskGridData: props.GridData,
                onCheckBoxChange: onCheckAllCheckBoxClicked,
                onClickRefreshBtn: onClickRefreshBtn,
              }),
          }}
          muiSelectCheckboxProps={(props) => ({
            component: () =>
              TableCellCheckBoxWithIconComponent({
                row: props.row,
                functionRight: {
                  AddBtn: showAddButton(),
                },
                onCheckBoxChange: onParentTaskCheckBoxClicked,
                onclickAddNewTaskCallBack: onclickAddNewTaskCallBack,
              }),
          })}
          displayColumnDefOptions={{
            "mrt-row-select": {
              muiTableHeadCellProps: {
                sx: {
                  paddingTop: "4px",
                },
                className: "select-all-column-head",
              },
              muiTableBodyCellProps: {
                className: "select-all-column-body",
              },
            },
            "mrt-row-expand": {
              muiTableHeadCellProps: {
                sx: {
                  paddingTop: "4px",
                  display: "none",
                },
                className: "expand-sec",
              },
              muiTableBodyCellProps: {
                className: "header-refresh",
                sx: {
                  display: "none",
                },
              },
            },
          }}
          muiTopToolbarProps={{
            className: "btn-bar-command",
          }}
          localization={{ filterFuzzy: "" }}
          sortDescFirst={false}
        />
      </div>
    </div>
  );
};

export default ViewRemediationTrackerTaskTable;
